import { useCallback, useRef, useState } from 'react';
import {
  Check, MoreVertical, Lock, Users, Building2, Eye,
  ArrowUpDown, ChevronUp, ChevronDown, ChevronRight,
} from '../../icons';
import { formatBytes, type NodePublic } from '../../api/workspace';
import { tWorkspace, type WorkspaceI18nKey } from '../../lib/workspace-i18n';
import { usageLabelOf, usageMetaOf } from './workspaceUsageMeta';
import { WorkspaceFileIcon } from './WorkspaceFileIcon';
import { useDragSelect } from '../../hooks/useDragSelect';
import { setWorkspaceNodes, hasExternalFiles, readExternalFiles } from '../../lib/workspace-dnd';
import type { SortField, SortOrder } from './WorkspaceSortMenu';
import { smartMetaOf, smartLabelOf } from './WorkspaceSmartFolders';
import type { UsageSource } from '../../api/workspace';
import { formatWorkspaceDateTime } from './workspace-datetime';

/**
 * 드라이브 목록 표 (2026-08-14) — 사용자 요구:
 *
 *   "테이블 형태로 폴더이름, 수정일, 파일크기, 공개여부가 나온다. 이름의 끝에는 설정이나
 *    메뉴 확대 아이콘이 있으며 …"
 *   "보기에는 목록, 간단한 목록, 타일이 있고"
 *
 * 기존 `FileGrid`/`FileListView` 는 그대로 둔다 — 저쪽은 선택·행 메뉴 개념이 없고, 여기에
 * 체크박스와 10종 메뉴를 얹으면 두 컴포넌트의 계약이 갈라진다. 셋을 한 컴포넌트의
 * **보기 모드**로 합치는 편이 정렬·선택 상태를 한 곳에서 관리하기 쉽다.
 */

export type ViewMode = 'list' | 'compact' | 'tile';

/** 공개 여부 배지 — 서버가 주는 ownerScope 와 공유 여부를 사람 말로 바꾼다. */
function visibilityOf(node: NodePublic): { key: WorkspaceI18nKey; tone: string; icon: typeof Lock } {
  if (node.systemKey === 'company-root' || node.ownerScope === 'company') {
    return { key: 'workspace.vis.company', tone: 'all', icon: Building2 };
  }
  if (node.ownerScope === 'team') {
    return { key: 'workspace.vis.team', tone: 'team', icon: Users };
  }
  /*
   * ★ 공유가 걸린 행은 «비공개» 가 아니다 (2026-10-27 사용자 지시 — 「최상단에서 체크했는데 하위 폴더·파일에
   *   적용이 안 된다」). 직접 건 것 · 상위 폴더에서 물려받은 것 둘 다 서버가 shareSummary 에 싣는다.
   */
  if ((node.shareSummary?.targetCount ?? 0) > 0) {
    return node.shareSummary?.hasCompany
      ? { key: 'workspace.vis.company', tone: 'all', icon: Building2 }
      : { key: 'workspace.vis.shared', tone: 'team', icon: Users };
  }
  return { key: 'workspace.vis.private', tone: '', icon: Lock };
}

/**
 * 사용처 배지 — 이 파일이 어디에 쓰이는지.
 *
 * 목록의 파일 대부분이 다른 화면(주로 채팅)에 붙어 있는 첨부인데 지금까지 화면에 아무 표시가
 * 없었다. 지워도 되는지 판단할 근거가 없어 "마음대로 지우는" 사고가 난다.
 * 숫자만으로는 스캔이 안 되므로 **종류 아이콘을 함께** 준다.
 *
 * 아이콘·이름 표는 `workspaceUsageMeta.ts` 한 곳에 있다 (2026-08-19) — 세부정보 패널과
 * 같은 것을 쓴다. 모르는 종류가 와도 usageMetaOf 가 받아 내므로 목록이 안 터진다.
 */
/** 목록의 날짜 열 — 초 단위는 의미가 없어 분까지만 보여 준다. */
function formatWhen(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return formatWorkspaceDateTime(d);
}

interface Props {
  nodes: NodePublic[];
  loading: boolean;
  view: ViewMode;
  /** 체크된 id 집합. 비어 있으면 선택 툴바가 뜨지 않는다. */
  selected: ReadonlySet<string>;
  /**
   * 이름을 눌러 **들여다보는 중인** 항목 (2026-08-14).
   *
   * 체크와 분리돼 있다 — 이름 클릭이 체크까지 하면 하나 열어 보려던 것이 곧 일괄 선택이
   * 되어 상단 줄이 액션 툴바로 바뀐다. 표시만 다르게 하고 체크박스는 건드리지 않는다.
   */
  focusedId?: string | null;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
  /**
   * 체크를 **정하는**(토글 아님) 함수 — 끌어서 여러 개 켜고 끄기에 쓴다 (2026-08-18).
   * 안 주면 지금까지처럼 클릭 토글만 된다(끌어도 아무 일도 안 일어난다).
   */
  onSetSelect?: (id: string, on: boolean) => void;
  /**
   * 끌어다 놓아 옮기기 (2026-08-18) — 폴더 위에 떨어뜨리면 그 안으로 옮긴다.
   * 안 주면 끌기 자체가 꺼진다(읽기 전용 화면·휴지통에서 그냥 안 주면 된다).
   */
  onDropMove?: (nodes: readonly NodePublic[], target: NodePublic) => void;
  /** 바깥에서 끌어온 파일을 이 폴더에 올린다 (2026-08-19). */
  onDropFiles?: (folderId: string, folderName: string, files: File[]) => void;
  /** 폴더 진입 — 폴더에만 쓴다(탐색이 주 목적이라 이름 클릭으로 들어간다). */
  onOpen: (node: NodePublic) => void;
  /**
   * 미리보기/편집기 열기 — **호버 시 나타나는 아이콘을 눌렀을 때만**.
   *
   * 예전에는 이름을 누르면 곧장 문서 패널이 떴다. 목록 대부분이 채팅 첨부라 훑어보려고
   * 누른 것뿐인데 매번 무거운 렌더러가 떠서 오탐이 잦았다.
   */
  onPreview: (node: NodePublic) => void;
  /** 행을 눌렀을 때 — 선택 + 세부정보. 미리보기는 위 onPreview 가 맡는다. */
  onSelect: (node: NodePublic) => void;
  /** ⋯ 클릭 — 앵커 요소를 함께 넘겨 메뉴를 그 아래에 붙인다. */
  onRowMenu: (node: NodePublic, anchor: HTMLElement) => void;
  /** 세 번째 열의 의미 — 화면마다 다르다(수정일 / 공유일 / 삭제일). */
  dateLabelKey?: WorkspaceI18nKey;
  /** 마지막 열을 '공개 여부' 대신 사람 이름으로 — 공유됨 화면. */
  personColumn?: { labelKey: WorkspaceI18nKey; nameOf: (node: NodePublic) => string };
  /**
   * 비었을 때의 문구 (2026-08-14, 기본 '폴더가 비어 있습니다').
   * 회사공유처럼 "폴더" 가 아닌 화면에서는 그 말이 상황을 잘못 설명한다.
   */
  emptyKey?: WorkspaceI18nKey;
  /**
   * 컬럼 머리글 정렬 (2026-08-14 2차, 선택).
   *
   * 넘기지 않으면 머리글은 지금까지처럼 **글자**다 — 정렬은 상단 '정렬' 메뉴로만 한다.
   * 넘기면 이름·수정일·크기 머리글이 버튼이 되고, 같은 축을 다시 누르면 방향이 뒤집힌다.
   *
   * ⚠ 판정은 서버가 한다(페이지네이션이라 클라이언트 정렬은 페이지 경계에서 깨진다).
   *   여기서는 **누른 사실만** 올려 보내고, 지금 어느 축인지 화살표로 보여 준다.
   */
  sortField?: SortField | null;
  sortOrder?: SortOrder;
  onSort?: (field: SortField) => void;
  /**
   * 날짜 열이 어떤 축으로 정렬되는가 (기본 'updated').
   * 공유됨 화면은 그 열이 '공유일' 이므로 부모가 'shared' 를 준다 — 라벨과 축을 맞춘다.
   */
  dateSortField?: SortField;
  /**
   * 출처별 모음을 **목록 맨 위에 폴더처럼** 그린다 (2026-08-21, 선택).
   *
   * 안 주면 지금까지와 똑같다. 내 파일 루트에서만 넘어온다 — 폴더 안이나 검색 중에는
   * 전체 기준 묶음이라 뜻이 흐려진다(서버도 그때만 `smartFolders` 를 보낸다).
   *
   * ## 왜 진짜 폴더가 아닌가
   *
   * 내 파일 루트의 98%가 다른 화면에서 흘러든 첨부다(운영 실측 351건 중 직접 올린 것 6건).
   * 그렇다고 파일을 실제로 옮길 수는 없다 — **업로드 시점에는 출처를 모르고**(참조는
   * 메시지가 전송돼야 생긴다), 한 파일이 채팅과 업무 양쪽에 쓰이면 부모는 하나뿐이라
   * 둘 중 하나를 골라야 한다. 가상 행은 양쪽에 다 보인다.
   *
   * 그래서 **보이는 것만** 폴더로 만든다. 데이터는 한 글자도 안 바뀐다.
   */
  sources?: ReadonlyArray<{ source: UsageSource; count: number }>;
  /** 출처 행을 눌렀을 때 — 그 출처 목록으로 들어간다. `sources` 와 함께 온다. */
  onOpenSource?: (source: UsageSource) => void;
}

/** `onSetSelect` 를 안 준 호출부용 — hook 은 조건 없이 불러야 하므로 빈 함수를 준다. */
const NOOP_SET_SELECT = () => {};

export function WorkspaceTable({
  nodes, loading, view, selected, focusedId = null, onToggleSelect, onToggleSelectAll, onSetSelect,
  onDropMove,
  onDropFiles,
  onOpen, onPreview, onSelect, onRowMenu,
  dateLabelKey = 'workspace.col.modified', personColumn,
  emptyKey = 'workspace.view.empty',
  sortField = null, sortOrder = 'asc', onSort, dateSortField = 'updated',
  sources, onOpenSource,
}: Props) {
  const allSelected = nodes.length > 0 && nodes.every((n) => selected.has(n.id));
  /** 출처 행을 그릴 수 있는가 — 둘 다 있어야 한다(목록만 주고 손잡이가 없으면 죽은 줄). */
  const srcRows = (sources && onOpenSource && sources.length > 0) ? sources : null;

  /**
   * 끌어서 켜고 끄기 (2026-08-18). 체크 칸에서 눌러 아래로 훑으면 지나는 행이 모두
   * **처음 누른 칸의 반대**가 된다(켜진 데서 시작하면 해지).
   */
  const drag = useDragSelect(onSetSelect ?? NOOP_SET_SELECT);

  /**
   * 끌어다 놓아 옮기기 (2026-08-18).
   *
   * ⚠ 체크 칸에서 시작한 것은 **칠하기**(useDragSelect)라 여기서 막는다. 한 행이 두 가지
   *   끌기를 다 갖고 있어서, 어느 쪽인지는 **어디를 잡았는가**로만 가른다.
   *
   * 무엇을 옮기는가 — 잡은 행이 **체크된 것 중 하나면 체크된 전부**, 아니면 그 행 하나.
   * (파일 탐색기와 같은 규칙. 체크해 놓고 다른 것을 끌면 그 하나만 간다.)
   *
   * 어디에 놓을 수 있는가 — **폴더**여야 하고, 끌고 있는 것 자신이면 안 된다(자기 안으로).
   * 더 깊은 순환(부모를 자식 안으로)은 목록에 없는 조상 정보가 필요해 서버가 막는다.
   */
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const draggingRef = useRef<readonly NodePublic[]>([]);

  const movingFor = useCallback((n: NodePublic): readonly NodePublic[] => (
    selected.has(n.id) ? nodes.filter((x) => selected.has(x.id)) : [n]
  ), [nodes, selected]);

  const canDropOn = useCallback((target: NodePublic): boolean => {
    if (!onDropMove || target.type !== 'folder') return false;
    return !draggingRef.current.some((n) => n.id === target.id);
  }, [onDropMove]);

  /** 바깥에서 끌어온 파일을 이 행(폴더)에 올릴 수 있는가. */
  const canDropFilesOn = useCallback((target: NodePublic, dt: DataTransfer | null): boolean => (
    !!onDropFiles && target.type === 'folder' && hasExternalFiles(dt)
  ), [onDropFiles]);

  const rowDragProps = useCallback((n: NodePublic) => {
    if (!onDropMove && !onDropFiles) return {};
    return {
      draggable: true,
      onDragStart: (e: React.DragEvent) => {
        // 체크 칸에서 시작한 끌기는 '칠하기' 다 — 옮기기로 가로채지 않는다.
        if ((e.target as HTMLElement).closest?.('.ws-cell-check')) { e.preventDefault(); return; }
        draggingRef.current = movingFor(n);
        e.dataTransfer.effectAllowed = 'move';
        /*
          짐을 dataTransfer 에도 싣는다 (2026-08-18) — 좌측 폴더 트리처럼 **다른 컴포넌트**가
          받을 때는 이 안의 ref 를 못 본다. 표 안에서 받을 때는 ref 를 그대로 쓴다
          (dragover 단계에서는 dataTransfer 내용을 못 읽어서, 목록 안 판정이 더 정확하다).
        */
        setWorkspaceNodes(
          e.dataTransfer,
          draggingRef.current.map((x) => x.id),
          draggingRef.current.map((x) => x.name),
        );
      },
      onDragEnd: () => { draggingRef.current = []; setDragOverId(null); },
      onDragOver: (e: React.DragEvent) => {
        const files = canDropFilesOn(n, e.dataTransfer);
        if (!files && !canDropOn(n)) return;
        // preventDefault 를 해야 '놓을 수 있는 곳' 이 된다(안 하면 drop 이 안 온다).
        e.preventDefault();
        e.dataTransfer.dropEffect = files ? 'copy' : 'move';
        if (dragOverId !== n.id) setDragOverId(n.id);
      },
      onDragLeave: () => setDragOverId((cur) => (cur === n.id ? null : cur)),
      onDrop: (e: React.DragEvent) => {
        if (canDropFilesOn(n, e.dataTransfer)) {
          e.preventDefault();
          e.stopPropagation();   // 본문 전체 드롭존이 또 받지 않게
          setDragOverId(null);
          const picked = readExternalFiles(e.dataTransfer);
          if (picked.length > 0) onDropFiles?.(n.id, n.name, picked);
          return;
        }
        if (!canDropOn(n)) return;
        e.preventDefault();
        const moving = draggingRef.current;
        draggingRef.current = [];
        setDragOverId(null);
        if (moving.length > 0) onDropMove?.(moving, n);
      },
    };
  }, [canDropOn, canDropFilesOn, dragOverId, movingFor, onDropMove, onDropFiles]);

  /**
   * 정렬 가능한 머리글 한 칸.
   *
   * `onSort` 가 없으면 예전처럼 **그냥 글자**를 그린다 — 버튼처럼 보이는데 눌러도 아무 일이
   * 없는 상태를 만들지 않기 위해서다.
   */
  const headCell = (field: SortField, labelKey: WorkspaceI18nKey) => {
    const label = tWorkspace(labelKey);
    if (!onSort) return label;
    const on = sortField === field;
    return (
      <button
        type="button"
        className={`ws-th-sort${on ? ' ws-th-sort--on' : ''}`}
        aria-label={tWorkspace('workspace.col.sort_aria', { col: label })}
        // 스크린리더가 "무엇으로 정렬돼 있나" 를 읽을 수 있는 표준 속성이다.
        aria-sort={on ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
        onClick={() => onSort(field)}
      >
        {label}
        {on
          ? (sortOrder === 'asc' ? <ChevronUp size={12} aria-hidden /> : <ChevronDown size={12} aria-hidden />)
          : <ArrowUpDown size={11} aria-hidden className="ws-th-sort-idle" />}
      </button>
    );
  };

  /** 사용처 셀 — 참조가 없으면 대시. */
  const usageCell = (n: NodePublic) => {
    if (!n.usage) return <span className="ws-usage ws-usage--none">—</span>;
    const meta = usageMetaOf(n.usage.kind);
    const Icon = meta.icon;
    const label = usageLabelOf(n.usage.kind);
    return (
      <span className="ws-usage" title={label}>
        <Icon size={12} aria-hidden />
        {label}
        <b className="ws-num">{n.usage.count}</b>
      </span>
    );
  };

  /**
   * 미리보기 버튼 — 호버 시 이름 끝에 나타난다.
   * ⚠ `:focus-visible` 에서도 보이게 CSS 를 잡았다. hover 전용이면 키보드 사용자는
   *   미리보기에 **영영 도달할 수 없다**.
   */
  const previewBtn = (n: NodePublic) => (
    <button
      type="button"
      className="ws-preview-btn"
      aria-label={tWorkspace('workspace.row.aria_preview', { name: n.name })}
      title={tWorkspace('workspace.row.preview')}
      onClick={(e) => { e.stopPropagation(); onPreview(n); }}
    >
      <Eye size={14} aria-hidden />
    </button>
  );

  /*
   * ⚠ 조기 반환은 **출처 행이 없을 때만** 한다 (2026-08-21).
   *   파일이 0건이어도 출처 모음은 보여야 한다 — 첨부만 있고 직접 올린 것이 없는
   *   계정(운영에 넷)에서는 이 행들이 화면의 전부다. 여기서 빠져나가면 그 사람들에게는
   *   내 파일이 통째로 빈 화면이 된다.
   */
  if (loading && nodes.length === 0 && !srcRows) {
    return <div className="workspace-grid-empty">{tWorkspace('workspace.grid.loading')}</div>;
  }
  if (!loading && nodes.length === 0 && !srcRows) {
    return <div className="workspace-grid-empty">{tWorkspace(emptyKey)}</div>;
  }

  if (view === 'tile') {
    return (
      <div className="ws-tiles">
        {srcRows?.map(({ source, count }) => {
          const Icon = smartMetaOf(source).icon;
          return (
            /*
              ⚠ 파일 타일과 **같은 태그**(role 을 얹은 div)여야 한다. `<button>` 으로 하면
                UA 기본 `align-items:center` 가 세로 플렉스 타일의 자식을 가운데로 몰아
                이 타일만 가운데 정렬로 튄다.
            */
            <div
              key={`src-${source}`}
              className="ws-tile ws-tile--src"
              role="button"
              tabIndex={0}
              onClick={() => onOpenSource?.(source)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenSource?.(source); } }}
            >
              <span className="ws-tile-thumb">
                <Icon size={30} aria-hidden />
              </span>
              <b className="ws-tile-name">{smartLabelOf(source)}</b>
              <span className="ws-tile-meta">
                {tWorkspace('workspace.src.count', { n: String(count) })}
              </span>
            </div>
          );
        })}
        {nodes.map((n) => (
          <div
            key={n.id}
            className={`ws-tile${selected.has(n.id) ? ' ws-tile--on' : ''}`
              + (n.id === focusedId ? ' ws-tile--focus' : '')
              + (dragOverId === n.id ? ' ws-tile--drop' : '')}
            role="button"
            {...rowDragProps(n)}
            tabIndex={0}
            // 표와 같은 규칙 — 클릭은 세부정보, 폴더만 더블클릭으로 진입.
            onClick={() => onSelect(n)}
            onDoubleClick={() => { if (n.type === 'folder') onOpen(n); }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              if (n.type === 'folder') onOpen(n); else onSelect(n);
            }}
          >
            <span className="ws-tile-thumb"><WorkspaceFileIcon node={n} size={30} /></span>
            <b className="ws-tile-name" title={n.name}>{n.name}</b>
            <span className="ws-tile-meta">
              {n.type === 'folder' ? formatWhen(n.updatedAt) : formatBytes(n.sizeBytes)}
              {n.usage && (
                <span className="ws-usage ws-usage--tile">
                  {usageLabelOf(n.usage.kind)} {n.usage.count}
                </span>
              )}
            </span>
            <span className="ws-tile-actions">
              {n.type !== 'folder' && previewBtn(n)}
              <button
                type="button"
                className="ws-row-menu"
                aria-label={tWorkspace('workspace.row.aria_menu', { name: n.name })}
                aria-haspopup="menu"
                onClick={(e) => { e.stopPropagation(); onRowMenu(n, e.currentTarget); }}
              >
                <MoreVertical size={16} />
              </button>
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className={`ws-table ws-table--${view}`}
      role="table"
      /*
       * 끌기가 끝나며 따라오는 click 을 여기서 한 번 삼킨다. 캡처 단계라 체크 상자의
       * 토글과 행 클릭(세부정보 열기)이 **함께** 막힌다 — 아래쪽에서 각자 막으려 하면
       * 어느 한쪽이 새어 방금 칠한 것이 도로 뒤집힌다.
       */
      onClickCapture={(e) => {
        if (!drag.consumeClick()) return;
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <div className="ws-thead" role="row">
        <span className="ws-cell-check" role="columnheader">
          <button
            type="button"
            className={`ws-check${allSelected ? ' ws-check--on' : ''}`}
            role="checkbox"
            aria-checked={allSelected}
            aria-label={tWorkspace('workspace.row.aria_select_all')}
            onClick={onToggleSelectAll}
          >
            <Check size={11} aria-hidden />
          </button>
        </span>
        <span role="columnheader">{headCell('name', 'workspace.col.name')}</span>
        <span className="ws-cell-usage" role="columnheader">{tWorkspace('workspace.col.usage')}</span>
        {/*
          날짜 열의 정렬 축은 **그 열이 무엇을 뜻하느냐**를 따른다 (2026-08-14 2차).
          공유됨 화면의 이 열은 '공유일' 이라 수정일 축으로 세우면 라벨과 결과가 어긋난다
          (그래서 한동안 버튼 자체를 뺐다). 서버에 공유일 축이 생겨 이제 되살렸다 —
          부모가 화면에 맞는 축을 준다.
        */}
        <span className="ws-cell-date" role="columnheader">{headCell(dateSortField, dateLabelKey)}</span>
        <span className="ws-cell-size" role="columnheader">{headCell('size', 'workspace.col.size')}</span>
        <span className="ws-cell-vis" role="columnheader">
          {tWorkspace(personColumn?.labelKey ?? 'workspace.col.visibility')}
        </span>
        <span role="columnheader" />
      </div>

      {/*
        출처별 모음 (2026-08-21) — 폴더처럼 보이지만 **가는 길**이다.
        체크·행 메뉴·끌기를 일부러 달지 않는다. 고를 수 있는 것처럼 보이면 '이동'·'삭제'
        같은 동작을 기대하게 되는데, 이 행 뒤에는 옮기거나 지울 실체가 없다.
      */}
      {srcRows?.map(({ source, count }) => {
        const Icon = smartMetaOf(source).icon;
        const label = smartLabelOf(source);
        return (
          <div
            key={`src-${source}`}
            className="ws-row ws-row--src"
            role="row"
            tabIndex={0}
            onClick={() => onOpenSource?.(source)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenSource?.(source); } }}
          >
            {/* 체크 칸은 자리만 비운다 — 없애면 이름이 폴더 행과 어긋난다. */}
            <span className="ws-cell-check" role="cell" aria-hidden />
            <span className="ws-cell-name" role="cell">
              <span className="ws-name-btn ws-name-btn--static">
                {/*
                  ⚠ 좌측 스마트 폴더처럼 `meta.tone` 을 쓰면 안 된다 — 그 값은
                    `var(--k-word)` 류인데 `--k-*` 는 `.workspace-filterbar` 와
                    `.ws-create-menu` **두 곳에만** 선언돼 있어 표 안에서는 해석되지 않는다
                    (실브라우저로 확인: 색이 그대로 검정으로 떨어진다). 죽은 색을 얹느니
                    currentColor 로 둔다 — 좌측 목록과도 이 편이 같아 보인다.
                */}
                <span className="ws-src-icon">
                  <Icon size={view === 'compact' ? 16 : 19} aria-hidden />
                </span>
                <span className="ws-name-txt"><b>{label}</b></span>
              </span>
              <span className="ws-src-count">{tWorkspace('workspace.src.count', { n: String(count) })}</span>
            </span>
            <span className="ws-cell-usage" role="cell" />
            <span className="ws-cell-date" role="cell" />
            <span className="ws-cell-size" role="cell" />
            <span className="ws-cell-vis" role="cell" />
            <span role="cell" className="ws-src-go" aria-hidden><ChevronRight size={15} /></span>
          </div>
        );
      })}

      {nodes.map((n) => {
        const vis = visibilityOf(n);
        const VisIcon = vis.icon;
        const on = selected.has(n.id);
        // 체크(on)와 열람 중(focus)은 별개다 — 같은 값으로 묶으면 이름 클릭이 체크가 된다.
        const focus = n.id === focusedId;
        return (
          <div
            key={n.id}
            className={`ws-row${on ? ' ws-row--on' : ''}${focus ? ' ws-row--focus' : ''}`
              + (dragOverId === n.id ? ' ws-row--drop' : '')}
            role="row"
            // 끌려 지나가는 대상은 **행 전체** — 체크 상자에만 달면 빨리 끌 때 행이 건너뛰어진다.
            {...drag.targetProps(n.id)}
            {...rowDragProps(n)}
            // 행을 누르면 **세부정보**다. 미리보기는 이름 끝의 아이콘만 연다(사용자 요구).
            onClick={() => onSelect(n)}
            // 폴더는 더블클릭으로 들어간다. 파일에는 더블클릭 동작을 두지 않는다 —
            // "그 아이콘을 클릭해야만 미리보기" 라는 요구와 어긋나기 때문.
            onDoubleClick={() => { if (n.type === 'folder') onOpen(n); }}
          >
            {/* 끌기는 체크 칸에서만 시작한다 — 이름·날짜에서 끌면 글자 선택이라야 한다. */}
            <span className="ws-cell-check" role="cell" {...drag.startProps(n.id, on)}>
              <button
                type="button"
                className={`ws-check${on ? ' ws-check--on' : ''}`}
                role="checkbox"
                aria-checked={on}
                aria-label={tWorkspace('workspace.row.aria_select', { name: n.name })}
                onClick={(e) => { e.stopPropagation(); onToggleSelect(n.id); }}
              >
                <Check size={11} aria-hidden />
              </button>
            </span>

            <span className="ws-cell-name" role="cell">
              <button
                type="button"
                className="ws-name-btn"
                // 폴더는 이름 클릭으로 들어간다(탐색이 주 목적). 파일은 세부정보.
                onClick={(e) => {
                  e.stopPropagation();
                  if (n.type === 'folder') onOpen(n); else onSelect(n);
                }}
              >
                <WorkspaceFileIcon node={n} size={view === 'compact' ? 16 : 19} />
                <span className="ws-name-txt">
                  <b title={n.name}>{n.name}</b>
                  {/*
                    문서 제목(OOXML dc:title)이 파일명과 다르면 함께 보여 준다.
                    ⚠ 검색이 name **과 docTitle 을 함께** 매칭하므로, 안 보여 주면
                      "왜 이 파일이 검색에 걸렸지" 를 알 수 없다(2026-08-12 도입 규약).
                  */}
                  {n.docTitle && n.docTitle !== n.name && (
                    <span className="ws-name-doctitle" title={n.docTitle}>{n.docTitle}</span>
                  )}
                </span>
              </button>
              {/* 미리보기는 파일만 — 폴더는 열 내용이 없다. */}
              {n.type !== 'folder' && previewBtn(n)}
            </span>

            <span className="ws-cell-usage" role="cell">{usageCell(n)}</span>
            <span className="ws-cell-date ws-num" role="cell">{formatWhen(n.updatedAt)}</span>
            <span className="ws-cell-size ws-num" role="cell">
              {n.type === 'folder' ? '—' : formatBytes(n.sizeBytes)}
            </span>
            <span className="ws-cell-vis" role="cell">
              {personColumn ? (
                personColumn.nameOf(n)
              ) : (
                <span className={`ws-pill${vis.tone ? ` ws-pill--${vis.tone}` : ''}`}>
                  <VisIcon size={11} aria-hidden />{tWorkspace(vis.key)}
                </span>
              )}
            </span>

            <span role="cell">
              <button
                type="button"
                className="ws-row-menu"
                aria-label={tWorkspace('workspace.row.aria_menu', { name: n.name })}
                aria-haspopup="menu"
                onClick={(e) => { e.stopPropagation(); onRowMenu(n, e.currentTarget); }}
              >
                <MoreVertical size={16} />
              </button>
            </span>
          </div>
        );
      })}
    </div>
  );
}
